import React, { useContext, useMemo, useState } from 'react';
import PageLayout from './page-layout';
import SlidePage from './organisim/slide-page';
import AvailabilityCalendar from './organisim/availability-calendar';
import { AppContext } from '../app';
import { useUser } from '../lib/hooks/use-user';
import { usePrivateLessonBookings, usePrivateLessonCoaches, usePrivateLessonSlots } from '../lib/hooks/use-private-lessons';
import { cancelPrivateLessonBooking, createPrivateLessonBooking } from '../lib/fetchers/private-lessons';
import { useMutation, useQueryClient } from 'react-query';
import Avatar from '@material-ui/core/Avatar';
import Button from '@material-ui/core/Button';
import Card from '@material-ui/core/Card';
import CardContent from '@material-ui/core/CardContent';
import Dialog from '@material-ui/core/Dialog';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import Divider from '@material-ui/core/Divider';
import List from '@material-ui/core/List';
import ListItem from '@material-ui/core/ListItem';
import ListItemAvatar from '@material-ui/core/ListItemAvatar';
import ListItemText from '@material-ui/core/ListItemText';
import Typography from '@material-ui/core/Typography';
import ChevronRight from '@material-ui/icons/ChevronRight';
import { Box, Flex } from '@rebass/grid';
import get from 'lodash.get';

const today = () => new Date().toISOString().slice(0, 10);
const futureDate = (days) => { const date = new Date(); date.setDate(date.getDate() + days); return date.toISOString().slice(0, 10); };
const displayTime = (value) => new Date(value).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' });
const dateKey = (value) => new Date(value).toISOString().slice(0, 10);

const PrivateLessons = () => {
  const { loggedInUserId } = useContext(AppContext);
  const { user } = useUser(loggedInUserId);
  const gymUid = get(user, 'gym.uuid');
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [selectedDay, setSelectedDay] = useState('');
  const [notice, setNotice] = useState('');
  const [pendingBooking, setPendingBooking] = useState(null);
  const [pendingCancellation, setPendingCancellation] = useState(null);
  const [hiddenSlotIds, setHiddenSlotIds] = useState([]);
  const { coaches, isLoading: coachesLoading } = usePrivateLessonCoaches(gymUid);
  const { slots, isLoading: slotsLoading } = usePrivateLessonSlots(
    gymUid,
    selectedCoach ? { coachUid: selectedCoach.uid, startDate: today(), endDate: futureDate(90) } : {},
    !!selectedCoach,
  );
  const { bookings } = usePrivateLessonBookings(gymUid);
  const queryClient = useQueryClient();
  const refresh = () => { queryClient.invalidateQueries(['private-lesson-slots', gymUid]); queryClient.invalidateQueries(['private-lesson-bookings', gymUid]); };
  const closeCoach = () => { setSelectedCoach(null); setSelectedDay(''); setPendingBooking(null); };
  const openCoach = (coach) => { setSelectedCoach(coach); setSelectedDay(''); setNotice(''); setHiddenSlotIds([]); };
  const book = useMutation((slotId) => createPrivateLessonBooking(gymUid, slotId, {}), {
    onSuccess: (_booking, slotId) => {
      setHiddenSlotIds((slotIds) => [...slotIds, slotId]);
      setPendingBooking(null);
      setNotice('Private lesson booked.');
      refresh();
    },
    onError: (error) => setNotice(error.message),
  });
  const cancel = useMutation((booking) => cancelPrivateLessonBooking(gymUid, booking.id), {
    onSuccess: (_response, booking) => {
      setPendingCancellation(null);
      setHiddenSlotIds((slotIds) => slotIds.filter((slotId) => slotId !== booking.slot.id));
      setNotice('Booking cancelled and the slot is available again.');
      refresh();
    },
    onError: (error) => setNotice(error.message),
  });
  // The API normally returns only available slots for a member. Keep this
  // client-side guard as well so an in-flight or stale response can never show
  // a time another member has already taken.
  const visibleSlots = useMemo(() => slots.filter((slot) => (
    slot.status === 'available' && !hiddenSlotIds.includes(slot.id)
  )), [slots, hiddenSlotIds]);
  const availableDays = useMemo(() => new Set(visibleSlots.map((slot) => dateKey(slot.starts_at))), [visibleSlots]);
  const daySlots = useMemo(() => visibleSlots.filter((slot) => dateKey(slot.starts_at) === selectedDay), [visibleSlots, selectedDay]);
  const activeBookings = useMemo(() => bookings.filter((booking) => booking.status === 'booked'), [bookings]);

  return (
    <PageLayout selected="Private Lessons" pageTitle="Private Lessons">
      <Box py={2}>
        <Typography variant="h5">Private lessons</Typography>
        <Typography color="textSecondary">Pick a coach to see their available lesson days.</Typography>
        <Card style={{ marginTop: 16 }}><CardContent>
          <Typography variant="h6">Coaches</Typography>
          {coachesLoading ? <Typography style={{ marginTop: 8 }}>Loading coaches...</Typography> : coaches.length === 0 ? <Typography color="textSecondary" style={{ marginTop: 8 }}>No coaches are available for private lessons yet.</Typography> : <List disablePadding>{coaches.map((coach, index) => <React.Fragment key={coach.uid}>{index > 0 && <Divider />}<ListItem button onClick={() => openCoach(coach)}><ListItemAvatar><Avatar src={coach.avatar_url}>{coach.name?.slice(0, 1)}</Avatar></ListItemAvatar><ListItemText primary={coach.name} secondary="View availability" /><ChevronRight color="action" /></ListItem></React.Fragment>)}</List>}
        </CardContent></Card>
        <Card style={{ marginTop: 16 }}><CardContent>
          <Typography variant="h6">My upcoming private lessons</Typography>
          {activeBookings.length === 0 ? <Typography color="textSecondary" style={{ marginTop: 8 }}>No private lessons booked.</Typography> : <List>{activeBookings.map((booking) => <React.Fragment key={booking.id}><ListItem><ListItemText primary={displayTime(booking.slot.starts_at)} secondary={`Coach ${booking.slot.coach.name}${booking.slot.location ? ` · ${booking.slot.location}` : ''}`} /><Button color="secondary" disabled={cancel.isLoading} onClick={() => setPendingCancellation(booking)}>Cancel</Button></ListItem><Divider /></React.Fragment>)}</List>}
        </CardContent></Card>
      </Box>

      <SlidePage open={!!selectedCoach} onClose={closeCoach} title={selectedCoach ? `${selectedCoach.name}'s availability` : 'Availability'}>
        {selectedCoach && <>
          <Flex alignItems="center" mb={2}><Avatar src={selectedCoach.avatar_url}>{selectedCoach.name?.slice(0, 1)}</Avatar><Box ml={2}><Typography variant="h6">{selectedCoach.name}</Typography><Typography color="textSecondary" variant="body2">Green dots mark days with open lesson times.</Typography></Box></Flex>
          {notice && <Typography color="primary">{notice}</Typography>}
          {slotsLoading ? <Typography style={{ marginTop: 16 }}>Loading availability...</Typography> : <>
            <AvailabilityCalendar availableDays={availableDays} selectedDay={selectedDay} onSelect={setSelectedDay} minDate={today()} maxDate={futureDate(90)} />
            <Typography variant="h6">{selectedDay ? `Times on ${selectedDay}` : 'Choose an available day'}</Typography>
            {!selectedDay ? <Typography color="textSecondary" style={{ marginTop: 8 }}>Dates without availability are crossed out.</Typography> : daySlots.length === 0 ? <Typography color="textSecondary" style={{ marginTop: 8 }}>No open times on this day.</Typography> : <List>{daySlots.map((slot) => <React.Fragment key={slot.id}><ListItem><ListItemText primary={`${displayTime(slot.starts_at)} – ${displayTime(slot.ends_at)}`} secondary={`${slot.location || 'Private lesson'}${slot.notes ? ` · ${slot.notes}` : ''}`} /><Button variant="contained" color="primary" disabled={book.isLoading} onClick={() => setPendingBooking(slot)}>Book</Button></ListItem><Divider /></React.Fragment>)}</List>}
          </>}
        </>}
      </SlidePage>

      <Dialog open={!!pendingBooking} onClose={() => !book.isLoading && setPendingBooking(null)} fullWidth maxWidth="xs">
        <DialogTitle>Book this private lesson?</DialogTitle>
        <DialogContent><Typography>{pendingBooking && `${displayTime(pendingBooking.starts_at)} with ${pendingBooking.coach.name}${pendingBooking.location ? ` at ${pendingBooking.location}` : ''}.`}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setPendingBooking(null)} disabled={book.isLoading}>Back</Button><Button variant="contained" color="primary" onClick={() => book.mutate(pendingBooking.id)} disabled={book.isLoading}>Confirm booking</Button></DialogActions>
      </Dialog>

      <Dialog open={!!pendingCancellation} onClose={() => !cancel.isLoading && setPendingCancellation(null)} fullWidth maxWidth="xs">
        <DialogTitle>Cancel this private lesson?</DialogTitle>
        <DialogContent><Typography>{pendingCancellation && `Cancel your ${displayTime(pendingCancellation.slot.starts_at)} lesson with ${pendingCancellation.slot.coach.name}? The time will become available again.`}</Typography></DialogContent>
        <DialogActions><Button onClick={() => setPendingCancellation(null)} disabled={cancel.isLoading}>Keep lesson</Button><Button color="secondary" onClick={() => cancel.mutate(pendingCancellation)} disabled={cancel.isLoading}>Cancel lesson</Button></DialogActions>
      </Dialog>
    </PageLayout>
  );
};

export default PrivateLessons;
